"use client";

import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Switch, Typography, Link as MuiLink } from "@mui/material";
import { useTranslations } from "next-intl";
import { useState, useEffect } from "react";
import CookieConsent, { Cookies, getCookieConsentValue } from "react-cookie-consent";
import { colors } from "../theme/colors";
import { updateGtmConsent, CONSENT_COOKIE_NAME as COOKIE_NAME, CONSENT_PREFS_KEY as COOKIE_PREFERENCES_NAME } from "../utils/consentMode";

// Global event to trigger cookie settings from anywhere
export const openCookieSettings = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('openCookieSettings'));
  }
};

/**
 * GDPR-compliant Cookie Banner Component
 * 
 * Features:
 * - Granular cookie control (Essential, Analytics, Marketing)
 * - Mobile-responsive design
 * - Equal prominence for Accept/Reject (GDPR requirement)
 * - Multilingual support (EN/DE)
 * - Accessibility compliant
 * - Integrates with Microsoft Clarity consent
 * - For logged-in users: Analytics is required per T&C (not optional)
 */
const CookieBanner = ({
  onConsentChange,
  isLoggedIn = false
}) => {
  const t = useTranslations("general");
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    // Always true, cannot be disabled
    analytics: isLoggedIn ? true : false,
    // True for logged-in users (T&C)
    marketing: false
  });
  // Track if banner should be hidden after custom save
  const [bannerVisible, setBannerVisible] = useState(true);

  // Keep a short defer so initial hero paint isn't interrupted, while still
  // showing consent almost immediately for UX/compliance.
  const [renderReady, setRenderReady] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setRenderReady(true), 500);
    return () => clearTimeout(id);
  }, []);

  // Load saved preferences from localStorage
  useEffect(() => {
    // Check if user already made a choice
    const existingConsent = getCookieConsentValue(COOKIE_NAME);
    if (existingConsent) {
      setBannerVisible(false);
    }
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_NAME);
    if (savedPreferences) {
      try {
        const preferences = JSON.parse(savedPreferences);
        setCookiePreferences({
          essential: true,
          // Always true
          analytics: isLoggedIn ? true : preferences.analytics || false,
          // Force true if logged in
          marketing: preferences.marketing || false
        });
      } catch (e) {
        console.error("Failed to parse cookie preferences:", e);
      }
    } else if (isLoggedIn) {
      // Logged-in users: analytics is always true (T&C)
      setCookiePreferences({
        essential: true,
        analytics: true,
        marketing: false
      });
    }
  }, [isLoggedIn]);

  // Listen for global event to open cookie settings
  useEffect(() => {
    const handleOpenSettings = () => {
      setShowSettings(true);
    };
    window.addEventListener('openCookieSettings', handleOpenSettings);
    return () => {
      window.removeEventListener('openCookieSettings', handleOpenSettings);
    };
  }, []);

  // Move Customize button into the action wrapper (desktop & mobile) for unified grouping
  useEffect(() => {
    function relocateCustomize() {
      if (typeof window === 'undefined') return;
      const customizeBtn = document.querySelector('.cookie-consent-customize-button');
      const wrapper = document.querySelector('.cookie-consent-button-wrapper');
      if (!customizeBtn || !wrapper) return;
      if (!wrapper.contains(customizeBtn)) {
        wrapper.appendChild(customizeBtn); // Append alongside existing Accept button
      }
    }
    // Initial attempt and slight delay to wait for library render
    relocateCustomize();
    const timeout = setTimeout(relocateCustomize, 50);
    window.addEventListener('resize', relocateCustomize);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', relocateCustomize);
    };
  }, []);

  // Save preferences to localStorage
  const savePreferences = preferences => {
    localStorage.setItem(COOKIE_PREFERENCES_NAME, JSON.stringify(preferences));
    setCookiePreferences(preferences);
  };

  // Handle Accept All
  const handleAcceptAll = () => {
    const preferences = {
      essential: true,
      analytics: true,
      marketing: true
    };
    savePreferences(preferences);

    // GTM Consent Mode v2
    updateGtmConsent({
      analytics: true,
      marketing: true
    });

    // Track in Application Insights
    if (typeof window !== 'undefined' && window.appInsights) {
      window.appInsights.trackEvent({
        name: 'CookieConsent',
        properties: {
          action: 'accept_all',
          essential: true,
          analytics: true,
          marketing: true
        }
      });
    }

    // Notify parent component (for Clarity integration)
    if (onConsentChange) {
      onConsentChange({
        analytics: true,
        marketing: true
      });
    }
  };

  // Handle Reject All (from modal)
  const handleRejectAllFromModal = () => {
    const analyticsAllowed = isLoggedIn ? true : false;
    const preferences = {
      essential: true,
      analytics: analyticsAllowed,
      marketing: false
    };
    savePreferences(preferences);
    setShowSettings(false);

    // Set the cookie to dismiss the banner
    Cookies.set(COOKIE_NAME, "true", {
      expires: 365
    });

    // Hide the banner
    setBannerVisible(false);

    // GTM Consent Mode v2
    updateGtmConsent({
      analytics: analyticsAllowed,
      marketing: false
    });

    // Track in Application Insights
    if (typeof window !== 'undefined' && window.appInsights) {
      window.appInsights.trackEvent({
        name: 'CookieConsent',
        properties: {
          action: 'reject_all',
          essential: true,
          analytics: analyticsAllowed,
          marketing: false
        }
      });
    }

    // Notify parent component (for Clarity integration)
    if (onConsentChange) {
      onConsentChange({
        analytics: analyticsAllowed,
        marketing: false
      });
    }
  };

  // Handle Save Custom Preferences
  const handleSavePreferences = () => {
    const preferencesToSave = {
      ...cookiePreferences,
      essential: true,
      analytics: isLoggedIn ? true : cookiePreferences.analytics
    };
    savePreferences(preferencesToSave);
    setShowSettings(false);

    // Set the cookie to dismiss the banner (same as Accept/Reject does)
    Cookies.set(COOKIE_NAME, "true", {
      expires: 365
    });

    // Hide the banner
    setBannerVisible(false);

    // GTM Consent Mode v2
    updateGtmConsent({
      analytics: preferencesToSave.analytics,
      marketing: preferencesToSave.marketing
    });

    // Track in Application Insights
    if (typeof window !== 'undefined' && window.appInsights) {
      window.appInsights.trackEvent({
        name: 'CookieConsent',
        properties: {
          action: 'customize',
          essential: preferencesToSave.essential,
          analytics: preferencesToSave.analytics,
          marketing: preferencesToSave.marketing
        }
      });
    }

    // Notify parent component (for Clarity integration)
    if (onConsentChange) {
      onConsentChange({
        analytics: preferencesToSave.analytics,
        marketing: preferencesToSave.marketing
      });
    }
  };

  // Handle preference toggle
  const handleToggle = category => {
    if (category === "essential") return; // Cannot disable essential cookies
    if (category === "analytics" && isLoggedIn) return; // Cannot disable analytics for logged-in users (T&C)

    setCookiePreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  return <>
      {renderReady && bannerVisible && <CookieConsent location="bottom" cookieName={COOKIE_NAME} expires={365} flipButtons={false} onAccept={handleAcceptAll} hideOnAccept={true} buttonText={t("CookieBannerAcceptAll")} ariaAcceptLabel={t("CookieBannerAcceptAll")} containerClasses="cookie-consent-container" buttonClasses="cookie-consent-accept-button" contentClasses="cookie-consent-content" disableStyles={true} style={{
      background: colors.background.paper,
      borderTop: `1px solid ${colors.border.light}`,
      boxShadow: colors.effects.boxShadow.banner,
      padding: "16px 20px",
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      // Keep below MUI modal (modal z-index ~1300). Set lower so settings dialog overlays correctly.
      zIndex: 1200,
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0
    }} buttonStyle={{
      background: colors.cta.main,
      color: colors.background.paper,
      fontSize: "14px",
      fontWeight: 600,
      padding: "10px 24px",
      borderRadius: "6px",
      border: "none",
      cursor: "pointer",
      transition: "background 0.2s ease",
      whiteSpace: "nowrap"
    }} contentStyle={{
      flex: "1 1 300px",
      margin: 0,
      fontSize: "14px",
      lineHeight: "1.5",
      color: colors.text.secondary
    }} buttonWrapperClasses="cookie-consent-button-wrapper" customButtonWrapperAttributes={{
      style: {
        display: "flex",
        gap: "8px",
        flexWrap: "wrap",
        alignItems: "center"
      }
    }}>
        {t("CookieBannerMessage")}
        {/* Customize button inline (desktop). Will be moved into action wrapper for mobile ordering. */}
        <button onClick={() => setShowSettings(true)} className="cookie-consent-customize-button" aria-label="Customize cookie preferences" style={{
        fontSize: "14px",
        fontWeight: 600,
        padding: "10px 24px",
        borderRadius: "6px",
        border: `1px solid ${colors.border.medium}`,
        background: colors.background.paper,
        color: colors.text.primary,
        whiteSpace: "nowrap",
        cursor: "pointer",
        transition: "all 0.2s ease"
      }}>
          {t("CookieBannerCustomize")}
        </button>
      </CookieConsent>}

      {/* Cookie Settings Modal */}
      <Dialog open={showSettings} onClose={() => setShowSettings(false)} maxWidth="sm" fullWidth PaperProps={{
      sx: {
        borderRadius: "8px",
        padding: "8px"
      }
    }} data-sentry-element="Dialog" data-sentry-source-file="CookieBanner.jsx">
        <DialogTitle sx={{
        fontWeight: 700,
        fontSize: "20px",
        color: colors.text.primary
      }} data-sentry-element="DialogTitle" data-sentry-source-file="CookieBanner.jsx">
          {t("CookieSettingsTitle")}
        </DialogTitle>
        <DialogContent dividers data-sentry-element="DialogContent" data-sentry-source-file="CookieBanner.jsx">
          <Box sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3
        }} data-sentry-element="Box" data-sentry-source-file="CookieBanner.jsx">
            {/* Essential Cookies */}
            <Box data-sentry-element="Box" data-sentry-source-file="CookieBanner.jsx">
              <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1
            }} data-sentry-element="Box" data-sentry-source-file="CookieBanner.jsx">
                <Typography variant="subtitle1" sx={{
                fontWeight: 600,
                color: colors.text.primary
              }} data-sentry-element="Typography" data-sentry-source-file="CookieBanner.jsx">
                  {t("CookieEssentialTitle")}
                </Typography>
                <FormControlLabel control={<Switch checked={true} disabled={true} sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: colors.cta.main
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: colors.cta.main
                }
              }} />} label="" sx={{
                margin: 0
              }} data-sentry-element="FormControlLabel" data-sentry-source-file="CookieBanner.jsx" />
              </Box>
              <Typography variant="body2" sx={{
              color: colors.text.tertiary,
              fontSize: "13px"
            }} data-sentry-element="Typography" data-sentry-source-file="CookieBanner.jsx">
                {t("CookieEssentialDescription")}
              </Typography>
            </Box>

            {/* Analytics Cookies */}
            <Box data-sentry-element="Box" data-sentry-source-file="CookieBanner.jsx">
              <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1
            }} data-sentry-element="Box" data-sentry-source-file="CookieBanner.jsx">
                <Typography variant="subtitle1" sx={{
                fontWeight: 600,
                color: colors.text.primary
              }} data-sentry-element="Typography" data-sentry-source-file="CookieBanner.jsx">
                  {t("CookieAnalyticsTitle")}
                  {isLoggedIn && <Typography component="span" variant="caption" sx={{
                  ml: 1,
                  color: colors.text.tertiary,
                  fontWeight: 400,
                  fontStyle: "italic"
                }}>
                      (Required)
                    </Typography>}
                </Typography>
                <FormControlLabel control={<Switch checked={cookiePreferences.analytics} onChange={() => handleToggle("analytics")} disabled={isLoggedIn} // Disabled for logged-in users
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: colors.cta.main
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: colors.cta.main
                }
              }} />} label="" sx={{
                margin: 0
              }} data-sentry-element="FormControlLabel" data-sentry-source-file="CookieBanner.jsx" />
              </Box>
              <Typography variant="body2" sx={{
              color: colors.text.tertiary,
              fontSize: "13px"
            }} data-sentry-element="Typography" data-sentry-source-file="CookieBanner.jsx">
                {t("CookieAnalyticsDescription")}
                {isLoggedIn && <Typography component="span" variant="body2" sx={{
                display: "block",
                mt: 1,
                color: colors.text.secondary,
                fontStyle: "italic"
              }}>
                    Analytics cookies are required for logged-in users as per our Terms & Conditions.
                  </Typography>}
              </Typography>
            </Box>

            {/* Marketing Cookies */}
            <Box data-sentry-element="Box" data-sentry-source-file="CookieBanner.jsx">
              <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1
            }} data-sentry-element="Box" data-sentry-source-file="CookieBanner.jsx">
                <Typography variant="subtitle1" sx={{
                fontWeight: 600,
                color: colors.text.primary
              }} data-sentry-element="Typography" data-sentry-source-file="CookieBanner.jsx">
                  {t("CookieMarketingTitle")}
                </Typography>
                <FormControlLabel control={<Switch checked={cookiePreferences.marketing} onChange={() => handleToggle("marketing")} sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: colors.cta.main
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: colors.cta.main
                }
              }} />} label="" sx={{
                margin: 0
              }} data-sentry-element="FormControlLabel" data-sentry-source-file="CookieBanner.jsx" />
              </Box>
              <Typography variant="body2" sx={{
              color: colors.text.tertiary,
              fontSize: "13px"
            }} data-sentry-element="Typography" data-sentry-source-file="CookieBanner.jsx">
                {t("CookieMarketingDescription")}
              </Typography>
            </Box>

            {/* Privacy Policy Link */}
            <Box sx={{
            mt: 2
          }} data-sentry-element="Box" data-sentry-source-file="CookieBanner.jsx">
              <Typography variant="body2" sx={{
              color: colors.text.tertiary
            }} data-sentry-element="Typography" data-sentry-source-file="CookieBanner.jsx">
                {t("CookieLearnMore")}:{" "}
                <MuiLink href="https://descripio.com/data-protection/" target="_blank" rel="noopener noreferrer" sx={{
                color: colors.primary.main,
                textDecoration: "underline",
                "&:hover": {
                  color: colors.primary.hover
                }
              }} data-sentry-element="MuiLink" data-sentry-source-file="CookieBanner.jsx">
                  {t("CookiePrivacyPolicy")}
                </MuiLink>
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{
        padding: "16px 24px",
        gap: 1,
        flexDirection: {
          xs: 'column',
          sm: 'row'
        },
        alignItems: {
          xs: 'stretch',
          sm: 'center'
        }
      }} data-sentry-element="DialogActions" data-sentry-source-file="CookieBanner.jsx">
          <Button onClick={handleRejectAllFromModal} sx={{
          color: colors.text.secondary,
          textTransform: "none",
          fontWeight: 600,
          border: `1px solid ${colors.border.medium}`,
          width: {
            xs: '100%',
            sm: 'auto'
          },
          "&:hover": {
            backgroundColor: colors.background.light
          }
        }} data-sentry-element="Button" data-sentry-source-file="CookieBanner.jsx">
            {t("CookieBannerRejectAll")}
          </Button>
          <Box sx={{
          flex: {
            xs: 0,
            sm: 1
          }
        }} data-sentry-element="Box" data-sentry-source-file="CookieBanner.jsx" />
          <Button onClick={() => setShowSettings(false)} sx={{
          color: colors.text.secondary,
          textTransform: "none",
          fontWeight: 600,
          width: {
            xs: '100%',
            sm: 'auto'
          }
        }} data-sentry-element="Button" data-sentry-source-file="CookieBanner.jsx">
            Cancel
          </Button>
          <Button onClick={handleSavePreferences} variant="contained" sx={{
          background: colors.cta.main,
          color: colors.background.paper,
          textTransform: "none",
          fontWeight: 600,
          width: {
            xs: '100%',
            sm: 'auto'
          },
          "&:hover": {
            background: colors.cta.hover
          }
        }} data-sentry-element="Button" data-sentry-source-file="CookieBanner.jsx">
            {t("CookieBannerSavePreferences")}
          </Button>
        </DialogActions>
      </Dialog>

  {/* Mobile-specific styles & ordering */}
      <style jsx global>{`
        .cookie-consent-customize-button:hover {
          background: ${colors.interactive.hover} !important;
          border-color: ${colors.border.focus} !important;
        }
        .cookie-consent-button-wrapper > * {
          flex: 1 1 auto;
        }
        /* Desktop & Mobile: Customize first, Accept second */
        .cookie-consent-customize-button { order: 1; }
        .cookie-consent-accept-button { order: 2; }
        
        @media (max-width: 768px) {
          .cookie-consent-container {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .cookie-consent-content {
    flex: 1 1 100% !important;
    margin-bottom: 8px !important; /* slightly tighter to reduce distance */
            display: flex !important;
            flex-direction: column !important;
            gap: 12px !important;
          }
          .cookie-consent-button-wrapper {
            width: 100% !important;
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }
          .cookie-consent-accept-button,
          .cookie-consent-customize-button {
            width: 100% !important;
            margin-left: 0 !important;
          }
        }
      `}</style>
    </>;
};
export default CookieBanner;